/**
 * «Размещение» менеджера (ядро агроцикла S2): таблица сезона, ввод, исправление,
 * изменение на земле, импорт файла, ошибки данных.
 *
 * Страница — это сводка и таблица: KPI (га по культурам, строки без хозяйства, правки за неделю),
 * фильтры (сезон, культура, хозяйство с поиском) и 50 строк на странице. В строке одно действие
 * «Исправить», остальное — в меню «⋯»; «Удалить» не стоит на виду и просит подтверждения. Формы,
 * импорт и севооборот контура открываются в боковом ящике, а не лентой под шапкой.
 *
 * Экран ничего не решает: права, отказы и «нельзя удалить» приходят с сервера. Кнопки внесения
 * видит только тот, кому сервер сказал `can_enter_placement` (менеджер с районной областью,
 * журнал №796); хоким и фермер читают.
 */

import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeftRight, FileUp, Plus, Sprout, Trash2 } from "lucide-react";
import { ApiError } from "../../api/client";
import { useRequest } from "../../api/useRequest";
import { useAnalysisContext } from "../../analysis/context";
import { useAuth } from "../../auth/context";
import { localIsoDate } from "../../lib/localDate";
import { useSliceDate } from "../../analysis/useSliceDate";
import { usePageTrail } from "../../astra/shell/ShellContext";
import { PageHeader } from "../../astra/ui/PageHeader";
import { Card, Stack } from "../../astra/ui/Card";
import { KpiCard } from "../../astra/ui/KpiCard";
import { DataTable, type Column } from "../../astra/ui/DataTable";
import { Dialog } from "../../astra/ui/Dialog";
import { Disclosure } from "../../astra/ui/Disclosure";
import { LoadError, failed } from "../../astra/ui/LoadError";
import { SkeletonKpi, SkeletonTable } from "../../astra/ui/Skeleton";
import { RowMenu, Segmented, Select, toast, type Choice, type RowMenuItem } from "../../astra/ui/forms";
import { num } from "../../astra/lib/format";
import { InfoPopover } from "../../components/InfoPopover";
import { defineMessages } from "../../i18n/define";
import { useT } from "../../i18n/hooks";
import { cropName } from "../../lib/crops";
import { type PlacementRow, placementApi } from "./placementApi";
import { loadSeasonRows } from "./placementLoad";
import { fieldLabel, formatHa, warningLabel } from "./placementModel";
import {
  FARM_NONE,
  NO_FILTERS,
  PAGE_SIZE,
  applyFilters,
  cropCards,
  hasFilters,
  pageOf,
  summarize,
  type Filters,
} from "./placementView";
import { CropTimelineDrawer, ImportDrawer, RowFormDrawer, type RowFormState } from "./PlacementDrawers";
import "./placement.css";

const m = defineMessages({
  trailLevel: { id: "placement.page.trailLevel", defaultMessage: "Район", description: "Название уровня в цепочке над заголовком страницы «Размещение»: район" },
  trailGroup: { id: "placement.page.trailGroup", defaultMessage: "Управление", description: "Название уровня в цепочке над заголовком страницы: раздел управления" },
  title: { id: "placement.page.title", defaultMessage: "Размещение", description: "Заголовок страницы менеджера: размещение культур по полям сезона" },
  purpose: { id: "placement.page.purpose", defaultMessage: "Какие поля какого хозяйства под какой культурой в сезоне (год посадки). Участники и земля агроцикла берутся отсюда — пересчитывать вручную не нужно.", description: "Пояснение под заголовком страницы «Размещение»" },
  addRow: { id: "placement.page.addRow", defaultMessage: "Внести строку", description: "Кнопка в шапке страницы «Размещение» (менеджер): внести строку" },
  importFileBtn: { id: "placement.page.importFileBtn", defaultMessage: "Импорт файла", description: "Кнопка в шапке страницы «Размещение» (менеджер): открыть импорт файла" },
  readOnly: { id: "placement.page.readOnly", defaultMessage: "Ваша роль смотрит, но не вносит: размещение вносит менеджер.", description: "Пометка под шапкой для хокима/фермера: внесение недоступно" },
  // --- KPI
  kpiRowsNote: { id: "placement.kpi.rowsNote", defaultMessage: "{count, plural, one {# строка} few {# строки} many {# строк} other {# строки}}", description: "Строка под числом KPI культуры: сколько строк размещения у культуры; # — число" },
  kpiRestLabel: { id: "placement.kpi.restLabel", defaultMessage: "Остальные культуры", description: "Подпись KPI: сумма площади по культурам, которые не поместились в ряд карточек" },
  kpiNoFarm: { id: "placement.kpi.noFarm", defaultMessage: "Строк без хозяйства", description: "Подпись KPI: сколько строк размещения не сопоставлены ни с одним хозяйством" },
  kpiNoFarmNote: { id: "placement.kpi.noFarmNote", defaultMessage: "{area} га", description: "Строка под KPI «Строк без хозяйства»: площадь этих строк в гектарах; {area} — число" },
  kpiNoFarmNone: { id: "placement.kpi.noFarmNone", defaultMessage: "все строки с хозяйством", description: "Строка под KPI «Строк без хозяйства», когда таких строк нет; строчными" },
  kpiEdits: { id: "placement.kpi.edits", defaultMessage: "Правок за неделю", description: "Подпись KPI: сколько строк размещения за последние 7 дней — правки (исправление ввода или изменение на земле), а не первичный ввод" },
  kpiEditsNote: { id: "placement.kpi.editsNote", defaultMessage: "исправления и изменения", description: "Строка под KPI «Правок за неделю»: что именно считается; строчными, коротко" },
  kpiEditsInfo: { id: "placement.kpi.editsInfo", defaultMessage: "Считаются строки, введённые за последние 7 дней, которые заменили другую строку (исправление ошибки ввода) или фиксируют изменение на земле (пересев, передача поля). Первичный ввод — вручную или файлом — не считается; строки без даты ввода (демо-слой, прежний ввод) тоже.", description: "Текст всплывающего пояснения KPI «Правок за неделю»: что считается и что не входит" },
  haUnit: { id: "placement.kpi.haUnit", defaultMessage: "га", description: "Единица у числа KPI: гектары; коротко" },
  // --- таблица
  tableTitle: { id: "placement.page.tableTitle", defaultMessage: "Строки размещения", description: "Заголовок карточки с таблицей размещения сезона" },
  filterSeason: { id: "placement.page.filterSeason", defaultMessage: "Сезон", description: "Подпись фильтра сезона над таблицей размещения" },
  filterCrop: { id: "placement.page.filterCrop", defaultMessage: "Культура", description: "Подпись фильтра культуры над таблицей размещения" },
  filterFarm: { id: "placement.page.filterFarm", defaultMessage: "Хозяйство", description: "Подпись выпадающего списка хозяйств над таблицей размещения" },
  allCrops: { id: "placement.page.allCrops", defaultMessage: "Все", description: "Вариант фильтра культуры: без фильтра; коротко" },
  allFarms: { id: "placement.page.allFarms", defaultMessage: "Все хозяйства", description: "Вариант списка хозяйств: без фильтра" },
  noFarmFilter: { id: "placement.page.noFarmFilter", defaultMessage: "Без хозяйства", description: "Вариант списка хозяйств: только строки без сопоставленного хозяйства" },
  farmRows: { id: "placement.page.farmRows", defaultMessage: "{count, plural, one {# строка} few {# строки} many {# строк} other {# строки}}", description: "Пояснение под хозяйством в списке фильтра: сколько у него строк; # — число" },
  farmSearch: { id: "placement.page.farmSearch", defaultMessage: "Найти хозяйство", description: "Подсказка в поле поиска хозяйства над таблицей размещения" },
  farmNothing: { id: "placement.page.farmNothing", defaultMessage: "Такого хозяйства в этом сезоне нет", description: "Пустой список поиска хозяйств над таблицей размещения" },
  reset: { id: "placement.page.reset", defaultMessage: "Сбросить фильтры", description: "Кнопка-ссылка над таблицей размещения: снять все фильтры; коротко" },
  total: { id: "placement.page.total", defaultMessage: "Строк: {count}", description: "Счётчик над таблицей размещения; {count} — число строк" },
  totalOf: { id: "placement.page.totalOf", defaultMessage: "Строк: {count} из {all}", description: "Счётчик над таблицей размещения при фильтре: найдено {count} из {all}; числа" },
  truncated: { id: "placement.page.truncated", defaultMessage: "Сезон больше потолка выгрузки: показаны первые {shown} из {total} строк, итоги в карточках неполные.", description: "Предупреждение над таблицей размещения: загружена только часть строк сезона; числа" },
  emptyRows: { id: "placement.page.emptyRows", defaultMessage: "В этом срезе строк размещения нет (демо-данные показываются, когда включён флаг «демо»).", description: "Пустое состояние таблицы размещения" },
  emptyTitle: { id: "placement.page.emptyTitle", defaultMessage: "Строк нет", description: "Заголовок пустого состояния таблицы размещения" },
  emptyFiltered: { id: "placement.page.emptyFiltered", defaultMessage: "под выбранные фильтры строк не нашлось", description: "Пояснение пустой таблицы размещения при включённых фильтрах; строчными" },
  loadWhat: { id: "placement.page.loadWhat", defaultMessage: "строки размещения", description: "Что не загрузилось, в винительном падеже, для сообщения «Не удалось загрузить …»; также подпись скелетона" },
  colField: { id: "placement.table.colField", defaultMessage: "Поле", description: "Заголовок колонки таблицы размещения: поле (контур и часть)" },
  colFarm: { id: "placement.table.colFarm", defaultMessage: "Хозяйство", description: "Заголовок колонки таблицы размещения" },
  colCrop: { id: "placement.table.colCrop", defaultMessage: "Культура", description: "Заголовок колонки таблицы размещения" },
  colArea: { id: "placement.table.colArea", defaultMessage: "Площадь", description: "Заголовок колонки таблицы размещения: площадь; единица «га» стоит рядом с заголовком" },
  colFrom: { id: "placement.table.colFrom", defaultMessage: "С даты", description: "Заголовок колонки таблицы размещения: с какой даты действует строка" },
  colBy: { id: "placement.table.colBy", defaultMessage: "Кто внёс", description: "Заголовок колонки таблицы размещения: кто внёс строку" },
  colActions: { id: "placement.table.colActions", defaultMessage: "Действия", description: "Заголовок колонки таблицы размещения с кнопками строки (виден только диктору)" },
  unmatchedFarm: { id: "placement.table.unmatchedFarm", defaultMessage: "не сопоставлено", description: "Ячейка «Хозяйство»: хозяйство строки не найдено в реестре; курсивом" },
  fromSeasonStart: { id: "placement.table.fromSeasonStart", defaultMessage: "с начала сезона", description: "Ячейка «С даты», когда строка действует с начала сезона" },
  demoMark: { id: "placement.table.demoMark", defaultMessage: "демо", description: "Ячейка «Кто внёс»: строка синтетическая (демо); строчная, коротко" },
  correct: { id: "placement.table.correct", defaultMessage: "Исправить", description: "Кнопка в строке таблицы размещения: исправить ошибку ввода; коротко" },
  change: { id: "placement.table.change", defaultMessage: "Изменение на земле", description: "Пункт меню строки таблицы размещения: изменение на земле (пересев, передача поля)" },
  menuTimeline: { id: "placement.table.menuTimeline", defaultMessage: "Севооборот контура", description: "Пункт меню строки таблицы размещения: открыть цепочку культур контура по времени" },
  remove: { id: "placement.table.remove", defaultMessage: "Удалить", description: "Пункт меню строки таблицы размещения: удалить строку; коротко" },
  removeLocked: { id: "placement.table.removeLocked", defaultMessage: "Удалить — по строке выданы обязательства", description: "Неактивный пункт меню строки: удалить нельзя, потому что по строке уже поручены работы на полях" },
  rowMenu: { id: "placement.table.rowMenu", defaultMessage: "Действия со строкой {field}", description: "aria-label кнопки «⋯» в строке таблицы размещения; {field} — номер контура (и часть)" },
  rowsOnPage: { id: "placement.table.rowsOnPage", defaultMessage: "{count, plural, one {строка на странице} few {строки на странице} many {строк на странице} other {строки на странице}}", description: "Знаменатель под таблицей размещения («50 строк на странице»); только слово, без числа" },
  pagerAria: { id: "placement.table.pagerAria", defaultMessage: "Страницы таблицы", description: "aria-label блока постраничного перехода под таблицей размещения" },
  pageOf: { id: "placement.table.pageOf", defaultMessage: "Страница {page} из {pages}", description: "Номер страницы под таблицей размещения; числа" },
  prev: { id: "placement.table.prev", defaultMessage: "Назад", description: "Кнопка постраничного перехода под таблицей: на предыдущую страницу" },
  next: { id: "placement.table.next", defaultMessage: "Дальше", description: "Кнопка постраничного перехода под таблицей: на следующую страницу" },
  // --- удаление
  deleteTitle: { id: "placement.delete.title", defaultMessage: "Удалить строку размещения?", description: "Заголовок окна подтверждения удаления строки размещения" },
  confirmDelete: { id: "placement.page.confirmDelete", defaultMessage: "Удалить строку {field} · {crop}?", description: "Вопрос подтверждения удаления строки размещения; {field} — номер контура, {crop} — культура" },
  deleteNote: { id: "placement.delete.note", defaultMessage: "Строку нельзя вернуть. Если нужно только исправить ошибку ввода — используйте «Исправить».", description: "Пояснение в окне подтверждения удаления строки размещения: удаление необратимо; «Исправить» — кнопка в строке таблицы" },
  deleted: { id: "placement.delete.done", defaultMessage: "Строка размещения удалена", description: "Уведомление после удаления строки размещения" },
  // --- ошибки данных
  conflictsTitle: { id: "placement.conflicts.title", defaultMessage: "Ошибки данных: размещение и землепользование расходятся", description: "Заголовок свёрнутого блока ошибок данных под таблицей размещения" },
  conflictsHint: { id: "placement.conflicts.hint", defaultMessage: "Работа адресуется землепользователю на дату; такие поля в охват кампании не попадают, пока размещение не исправлено. Список видно до старта цикла и запуска кампании.", description: "Пояснение под заголовком блока ошибок данных размещения" },
  conflictsMore: { id: "placement.conflicts.more", defaultMessage: "и ещё {count}", description: "Хвост списка ошибок данных: сколько ещё не показано; {count} — число" },
});

function errorText(cause: unknown): string {
  return cause instanceof ApiError ? cause.message : String(cause);
}

type Drawer =
  | { kind: "form"; form: RowFormState }
  | { kind: "import" }
  | { kind: "timeline"; row: PlacementRow }
  | null;

export function PlacementManager() {
  const t = useT();
  const analysis = useAnalysisContext();
  const { principal } = useAuth();
  const canEdit = principal?.can_enter_placement === true;
  const [season, setSeason] = useState("");
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [page, setPage] = useState(0);
  const [drawer, setDrawer] = useState<Drawer>(null);
  const [toDelete, setToDelete] = useState<PlacementRow | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  // «Сегодня» — число сервера (срез), а не часы браузера; часы — страховка на миг до ответа.
  const today = useSliceDate() ?? localIsoDate();

  usePageTrail([
    { level: t(m.trailLevel), name: t(m.trailLevel), to: "/" },
    { level: t(m.trailGroup), name: t(m.title) },
  ]);

  const data = useRequest(() => loadSeasonRows(analysis.params, season), [analysis.key, season]);
  const effectiveSeason = data.data?.season ?? season;
  const conflicts = useRequest(
    () => (effectiveSeason ? placementApi.conflicts({ ...analysis.params, season: effectiveSeason }) : Promise.resolve({ warnings: [], total: 0 })),
    [analysis.key, effectiveSeason],
  );

  const refresh = useCallback(() => {
    data.reload();
    conflicts.reload();
  }, [data, conflicts]);

  const all = data.data?.rows;
  const summary = useMemo(() => (all ? summarize(all, today) : null), [all, today]);
  const filtered = useMemo(() => (all ? applyFilters(all, filters, today) : []), [all, filters, today]);
  const shown = useMemo(() => pageOf(filtered, page), [filtered, page]);

  const warningByDetail = useMemo(() => {
    const map = new Map<number, string>();
    for (const w of conflicts.data?.warnings ?? []) map.set(w.detail_id, w.code);
    return map;
  }, [conflicts.data]);

  const patchFilters = (next: Partial<Filters>) => {
    setFilters((prev) => ({ ...prev, ...next }));
    setPage(0);
  };
  const pickSeason = (next: string) => {
    setSeason(next);
    setFilters(NO_FILTERS);
    setPage(0);
  };

  async function remove(row: PlacementRow) {
    setToDelete(null);
    setRowError(null);
    try {
      await placementApi.remove(row.id, analysis.params);
      toast.show({ message: t(m.deleted) });
      refresh();
    } catch (cause) {
      setRowError(errorText(cause));
    }
  }

  const rowMenu = (row: PlacementRow): RowMenuItem[] => [
    { id: "timeline", label: t(m.menuTimeline), icon: Sprout, onAction: () => setDrawer({ kind: "timeline", row }) },
    ...(canEdit && !row.is_demo
      ? [
          { id: "change", label: t(m.change), icon: ArrowLeftRight, onAction: () => setDrawer({ kind: "form", form: { kind: "change", row } }) },
          {
            id: "remove",
            label: row.locked ? t(m.removeLocked) : t(m.remove),
            icon: Trash2,
            tone: "danger" as const,
            separatorBefore: true,
            isDisabled: row.locked,
            onAction: () => setToDelete(row),
          },
        ]
      : []),
  ];

  const columns: Column<PlacementRow>[] = [
    {
      key: "field",
      header: t(m.colField),
      render: (row) => (
        <Link to={{ pathname: `/contours/${row.contour_id}`, search: analysis.linkSearch() }}>{fieldLabel(row)}</Link>
      ),
    },
    {
      key: "farm",
      header: t(m.colFarm),
      render: (row) => (
        <span className="pl-cell">
          {row.actor_id !== null ? (
            <Link to={{ pathname: `/farms/${row.actor_id}`, search: analysis.linkSearch() }}>{row.actor_name ?? `#${row.actor_id}`}</Link>
          ) : (
            <em>{t(m.unmatchedFarm)}</em>
          )}
          {warningByDetail.has(row.id) ? <span className="pl-flag">{warningLabel(warningByDetail.get(row.id)!)}</span> : null}
        </span>
      ),
    },
    { key: "crop", header: t(m.colCrop), render: (row) => cropName(row.crop_code, row.crop_name) },
    { key: "area", header: t(m.colArea), unit: t(m.haUnit), numeric: true, render: (row) => formatHa(row.area_microha) },
    { key: "from", header: t(m.colFrom), render: (row) => row.valid_from ?? t(m.fromSeasonStart) },
    { key: "by", header: t(m.colBy), render: (row) => (row.is_demo ? t(m.demoMark) : (row.entered_by ?? "—")) },
    {
      key: "actions",
      header: t(m.colActions),
      width: "1%",
      render: (row) => (
        <span className="pl-actions">
          {canEdit && !row.is_demo ? (
            <button type="button" className="btn btn--secondary btn--sm focus-ring" onClick={() => setDrawer({ kind: "form", form: { kind: "correction", row } })}>
              {t(m.correct)}
            </button>
          ) : null}
          <RowMenu size="sm" aria-label={t(m.rowMenu, { field: fieldLabel(row) })} items={rowMenu(row)} />
        </span>
      ),
    },
  ];

  // --- KPI
  const cards = summary ? cropCards(summary.crops) : [];
  const kpiCount = cards.length + 2;

  const seasons = data.data?.seasons ?? [];
  const cropChoices = summary?.crops ?? [];
  const cropOptions = [
    { id: "all", label: t(m.allCrops), count: summary?.rows },
    ...cropChoices.map((c) => ({ id: c.code, label: cropName(c.code, c.name), count: c.rows })),
  ];
  const farmOptions: Choice[] = summary
    ? [
        { id: "", name: t(m.allFarms) },
        ...(summary.noFarmRows > 0 ? [{ id: FARM_NONE, name: t(m.noFarmFilter), description: t(m.farmRows, { count: summary.noFarmRows }) }] : []),
        ...summary.farms.map((f) => ({ id: String(f.id), name: f.name, description: t(m.farmRows, { count: f.rows }) })),
      ]
    : [];

  return (
    <Stack>
      <PageHeader
        level="district"
        title={t(m.title)}
        purpose={t(m.purpose)}
        actions={
          canEdit ? (
            <>
              <button type="button" className="btn btn--primary focus-ring" onClick={() => setDrawer({ kind: "form", form: { kind: "create" } })}>
                <Plus size={16} aria-hidden /> {t(m.addRow)}
              </button>
              <button type="button" className="btn btn--secondary focus-ring" onClick={() => setDrawer({ kind: "import" })}>
                <FileUp size={16} aria-hidden /> {t(m.importFileBtn)}
              </button>
            </>
          ) : null
        }
      />

      {!canEdit ? (
        <p className="t-caption" role="note">
          {t(m.readOnly)}
        </p>
      ) : null}

      {failed(data) ? (
        <LoadError what={t(m.loadWhat)} error={data.error!} onRetry={data.reload} />
      ) : !summary ? (
        <>
          <div className="pl-kpis" data-count="5">
            {[0, 1, 2, 3, 4].map((i) => (
              <SkeletonKpi key={i} quiet />
            ))}
          </div>
          <SkeletonTable label={t(m.loadWhat)} rows={8} cols={6} />
        </>
      ) : (
        <>
          <div className="pl-kpis" data-count={Math.min(5, Math.max(3, kpiCount))} data-guide="placement-kpis">
            {cards.map((c) => {
              const known = c.code !== "";
              return (
                <KpiCard
                  key={c.code || "rest"}
                  label={known ? cropName(c.code, c.name) : t(m.kpiRestLabel)}
                  value={formatHa(c.microha)}
                  unit={t(m.haUnit)}
                  note={t(m.kpiRowsNote, { count: c.rows })}
                  {...(known ? { onClick: () => patchFilters({ crop: filters.crop === c.code ? "" : c.code }), pressed: filters.crop === c.code } : {})}
                />
              );
            })}
            <KpiCard
              label={t(m.kpiNoFarm)}
              value={num(summary.noFarmRows)}
              tone={summary.noFarmRows > 0 ? "warning" : undefined}
              note={summary.noFarmRows > 0 ? t(m.kpiNoFarmNote, { area: formatHa(summary.noFarmMicroha) }) : t(m.kpiNoFarmNone)}
              {...(summary.noFarmRows > 0
                ? { onClick: () => patchFilters({ farm: filters.farm === FARM_NONE ? "" : FARM_NONE }), pressed: filters.farm === FARM_NONE }
                : {})}
            />
            <KpiCard
              label={t(m.kpiEdits)}
              value={num(summary.recentRows)}
              note={t(m.kpiEditsNote)}
              info={<InfoPopover icon="info" label={t(m.kpiEdits)}>{t(m.kpiEditsInfo)}</InfoPopover>}
              {...(summary.recentRows > 0 ? { onClick: () => patchFilters({ recent: !filters.recent }), pressed: filters.recent } : {})}
            />
          </div>

          <div data-ai-region="placement-table">
            <Card
              size="lg"
              title={t(m.tableTitle)}
              aside={
                <span className="t-caption">
                  {hasFilters(filters) ? t(m.totalOf, { count: num(filtered.length), all: num(summary.rows) }) : t(m.total, { count: num(summary.rows) })}
                </span>
              }
            >
              <div className="pl-filters">
                {seasons.length > 1 ? (
                  <Segmented
                    aria-label={t(m.filterSeason)}
                    size="sm"
                    value={effectiveSeason}
                    onChange={pickSeason}
                    options={seasons.map((s) => ({ id: s, label: s }))}
                  />
                ) : null}
                {cropChoices.length > 1 && cropChoices.length <= 3 ? (
                  <Segmented
                    aria-label={t(m.filterCrop)}
                    size="sm"
                    value={filters.crop === "" ? "all" : filters.crop}
                    onChange={(id) => patchFilters({ crop: id === "all" ? "" : id })}
                    options={cropOptions}
                  />
                ) : cropChoices.length > 3 ? (
                  <Select
                    aria-label={t(m.filterCrop)}
                    size="sm"
                    value={filters.crop}
                    onChange={(id) => patchFilters({ crop: id })}
                    options={[{ id: "", name: t(m.allCrops) }, ...cropChoices.map((c) => ({ id: c.code, name: cropName(c.code, c.name) }))]}
                  />
                ) : null}
                <Select
                  aria-label={t(m.filterFarm)}
                  size="sm"
                  className="pl-filters__farm"
                  value={filters.farm}
                  onChange={(id) => patchFilters({ farm: id })}
                  options={farmOptions}
                  searchable
                  placeholder={t(m.farmSearch)}
                  emptyText={t(m.farmNothing)}
                />
                {hasFilters(filters) ? (
                  <button type="button" className="linkish focus-ring" onClick={() => patchFilters(NO_FILTERS)}>
                    {t(m.reset)}
                  </button>
                ) : null}
              </div>

              {data.data?.truncated ? (
                <p className="pl-hint" role="note">
                  {t(m.truncated, { shown: num(summary.rows), total: num(data.data.total) })}
                </p>
              ) : null}
              {rowError ? (
                <p role="alert" className="pl-error">
                  {rowError}
                </p>
              ) : null}

              <div className="pl-table" aria-busy={data.stale || undefined}>
                <DataTable
                  columns={columns}
                  rows={shown.items}
                  getKey={(row) => String(row.id)}
                  limit={PAGE_SIZE}
                  hardCap={PAGE_SIZE}
                  denominator={m.rowsOnPage}
                  empty={{ title: t(m.emptyTitle), reason: hasFilters(filters) ? t(m.emptyFiltered) : t(m.emptyRows) }}
                />
              </div>

              {shown.pages > 1 ? (
                <nav className="pl-pager" aria-label={t(m.pagerAria)}>
                  <span className="t-caption">{t(m.pageOf, { page: num(shown.page + 1), pages: num(shown.pages) })}</span>
                  <span className="pl-pager__buttons">
                    <button type="button" className="btn btn--secondary btn--sm focus-ring" disabled={shown.page === 0} onClick={() => setPage(shown.page - 1)}>
                      {t(m.prev)}
                    </button>
                    <button type="button" className="btn btn--secondary btn--sm focus-ring" disabled={shown.page >= shown.pages - 1} onClick={() => setPage(shown.page + 1)}>
                      {t(m.next)}
                    </button>
                  </span>
                </nav>
              ) : null}
            </Card>
          </div>

          {conflicts.data && conflicts.data.total > 0 ? (
            <div data-ai-region="placement-conflicts">
              <Card size="lg">
                <Disclosure label={t(m.conflictsTitle)} summary={num(conflicts.data.total)}>
                  <p className="pl-hint">{t(m.conflictsHint)}</p>
                  <ul className="pl-conflicts">
                    {conflicts.data.warnings.slice(0, 50).map((w) => (
                      <li key={`${w.detail_id}-${w.code}`}>{w.message}</li>
                    ))}
                  </ul>
                  {conflicts.data.total > 50 ? <p className="pl-hint">{t(m.conflictsMore, { count: num(conflicts.data.total - 50) })}</p> : null}
                </Disclosure>
              </Card>
            </div>
          ) : null}
        </>
      )}

      {data.data && drawer?.kind === "form" ? (
        <RowFormDrawer
          form={drawer.form}
          season={effectiveSeason}
          seasons={seasons}
          cropCodes={cropChoices.map((c) => ({ code: c.code, name: c.name }))}
          onClose={() => setDrawer(null)}
          onDone={() => {
            setDrawer(null);
            refresh();
          }}
        />
      ) : null}
      {data.data && drawer?.kind === "import" && canEdit ? (
        <ImportDrawer season={effectiveSeason} seasons={seasons} onClose={() => setDrawer(null)} onDone={refresh} />
      ) : null}
      {drawer?.kind === "timeline" ? (
        <CropTimelineDrawer row={drawer.row} params={analysis.params} linkSearch={analysis.linkSearch()} onClose={() => setDrawer(null)} />
      ) : null}

      {toDelete ? (
        <Dialog
          danger
          title={t(m.deleteTitle)}
          confirmLabel={t(m.remove)}
          onConfirm={() => void remove(toDelete)}
          onClose={() => setToDelete(null)}
        >
          <p>{t(m.confirmDelete, { field: fieldLabel(toDelete), crop: cropName(toDelete.crop_code, toDelete.crop_name) })}</p>
          <p className="t-caption">{t(m.deleteNote)}</p>
        </Dialog>
      ) : null}
    </Stack>
  );
}

export default PlacementManager;
